import { COUNTRY_CODES, DEPOT_CREATE } from '@constants';
import { DepotFormValues } from '@forms/DepotForm/types';
import { DepotForm } from '@forms/DepotForm/DepotForm';
import { useAuthHttp } from '@hooks';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDocumentTitle } from 'usehooks-ts';

function buildDepotPayload(data: DepotFormValues, isDraft: boolean) {
  return {
    depot: {
      isDraft,
      name: data.name,
      description: data.description,
      address: data.address,
      city: data.city,
      country: data.country,
      location: data.location,
      contactName: data.contactName,
      contactEmail: data.contactEmail,
      phoneNumber: {
        countryCode: COUNTRY_CODES.find(
          (country) => country.country_code === data.phoneCountryCode
        )?.country_code,
        countryNumberPrefix: COUNTRY_CODES.find(
          (country) => country.country_code === data.phoneCountryCode
        )?.phone_code.toString(),
        number: data.phoneNumber,
      },
      loadingDocks: data.loadingDocks ? Number(data.loadingDocks) : 0,
      standardBusinessHoursType: data.schedule?.value,
      hasFlexibleBusinessHours: data.flexibleHoursOnDemand,
      customBusinessHours:
        data.schedule?.value === 'Other'
          ? [
              {
                day: 'Monday',
                open: !data.mondayWorkingHours.working
                  ? '00:00:00'
                  : data.mondayWorkingHours.openingTime,
                close: !data.mondayWorkingHours.working
                  ? '00:00:00'
                  : data.mondayWorkingHours.closingTime,
              },
              {
                day: 'Tuesday',
                open: !data.tuesdayWorkingHours.working
                  ? '00:00:00'
                  : data.tuesdayWorkingHours.openingTime,
                close: !data.tuesdayWorkingHours.working
                  ? '00:00:00'
                  : data.tuesdayWorkingHours.closingTime,
              },
              {
                day: 'Wednesday',
                open: !data.wednesdayWorkingHours.working
                  ? '00:00:00'
                  : data.wednesdayWorkingHours.openingTime,
                close: !data.wednesdayWorkingHours.working
                  ? '00:00:00'
                  : data.wednesdayWorkingHours.closingTime,
              },
              {
                day: 'Thursday',
                open: !data.thursdayWorkingHours.working
                  ? '00:00:00'
                  : data.thursdayWorkingHours.openingTime,
                close: !data.thursdayWorkingHours.working
                  ? '00:00:00'
                  : data.thursdayWorkingHours.closingTime,
              },
              {
                day: 'Friday',
                open: !data.fridayWorkingHours.working
                  ? '00:00:00'
                  : data.fridayWorkingHours.openingTime,
                close: !data.fridayWorkingHours.working
                  ? '00:00:00'
                  : data.fridayWorkingHours.closingTime,
              },
              {
                day: 'Saturday',
                open: !data.saturdayWorkingHours.working
                  ? '00:00:00'
                  : data.saturdayWorkingHours.openingTime,
                close: !data.saturdayWorkingHours.working
                  ? '00:00:00'
                  : data.saturdayWorkingHours.closingTime,
              },
              {
                day: 'Sunday',
                open: !data.sundayWorkingHours.working
                  ? '00:00:00'
                  : data.sundayWorkingHours.openingTime,
                close: !data.sundayWorkingHours.working
                  ? '00:00:00'
                  : data.sundayWorkingHours.closingTime,
              },
            ]
          : [],
      vehicleLoadingTimeWindows: [
        {
          day: 'Monday',
          open: !data.mondayLoadingHours.operating
            ? '00:00:00'
            : data.mondayLoadingHours.openingTime,
          close: !data.mondayLoadingHours.operating
            ? '00:00:00'
            : data.mondayLoadingHours.closingTime,
        },
        {
          day: 'Tuesday',
          open: !data.tuesdayLoadingHours.operating
            ? '00:00:00'
            : data.tuesdayLoadingHours.openingTime,
          close: !data.tuesdayLoadingHours.operating
            ? '00:00:00'
            : data.tuesdayLoadingHours.closingTime,
        },
        {
          day: 'Wednesday',
          open: !data.wednesdayLoadingHours.operating
            ? '00:00:00'
            : data.wednesdayLoadingHours.openingTime,
          close: !data.wednesdayLoadingHours.operating
            ? '00:00:00'
            : data.wednesdayLoadingHours.closingTime,
        },
        {
          day: 'Thursday',
          open: !data.thursdayLoadingHours.operating
            ? '00:00:00'
            : data.thursdayLoadingHours.openingTime,
          close: !data.thursdayLoadingHours.operating
            ? '00:00:00'
            : data.thursdayLoadingHours.closingTime,
        },
        {
          day: 'Friday',
          open: !data.fridayLoadingHours.operating
            ? '00:00:00'
            : data.fridayLoadingHours.openingTime,
          close: !data.fridayLoadingHours.operating
            ? '00:00:00'
            : data.fridayLoadingHours.closingTime,
        },
        {
          day: 'Saturday',
          open: !data.saturdayLoadingHours.operating
            ? '00:00:00'
            : data.saturdayLoadingHours.openingTime,
          close: !data.saturdayLoadingHours.operating
            ? '00:00:00'
            : data.saturdayLoadingHours.closingTime,
        },
        {
          day: 'Sunday',
          open: !data.sundayLoadingHours.operating
            ? '00:00:00'
            : data.sundayLoadingHours.openingTime,
          close: !data.sundayLoadingHours.operating
            ? '00:00:00'
            : data.sundayLoadingHours.closingTime,
        },
      ],
      fleet: [
        { type: 'MultiAxleArticMoreThan26t', count: data.articsOver26t },
        { type: 'ThreeAxleRigidLessThan26t', count: data.rigidsUnder26t },
        { type: 'TwoAxleRigid18t', count: data.rigidsUnder18t },
        {
          type: 'TwoAxleRigidLessThan7AndHalf5t',
          count: data.rigidsUnder7_5t,
        },
        { type: 'LgvLessThan3AndHalft', count: data.ldvsUnder3_5t },
        { type: 'VanLessThan1AndHalft', count: data.vansUnder1_5t },
        { type: 'SpecialOrOther', count: data.specialOther },
      ],
    },
    images:
      data.depotImages?.map((image) => ({
        id: crypto.randomUUID(),
        title: image.name,
        image,
      })) ?? [],
  };
}

export function CreateDepot() {
  useDocumentTitle('FLOX - Create Depot');
  const navigate = useNavigate();
  const authHttpFormData = useAuthHttp('multipart/form-data');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  const pages = [
    { name: 'Home', href: '/', current: false },
    { name: 'Depots', href: '/depot-master', current: false },
    { name: 'Add New Depot', href: '/depot/create', current: true },
  ];

  const handleOnSubmit = async (data: DepotFormValues) => {
    setIsSubmitting(true);
    try {
      const payload = buildDepotPayload(data, false);
      await authHttpFormData.post(DEPOT_CREATE, payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      navigate('/depot-master');
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOnSaveDraft = async (data: DepotFormValues) => {
    setIsSavingDraft(true);
    try {
      const payload = buildDepotPayload(data, true);
      await authHttpFormData.post(DEPOT_CREATE, payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      navigate('/depot-master');
    } catch (error) {
      console.error(error);
    } finally {
      setIsSavingDraft(false);
    }
  };

  return (
    <DepotForm
      onSubmit={handleOnSubmit}
      onSaveDraft={handleOnSaveDraft}
      isSubmitting={isSubmitting}
      isSavingDraft={isSavingDraft}
      pages={pages}
      header='New Depot'
    />
  );
}
